import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import Cancel from '@mui/icons-material/Cancel';
import {
  chipClasses,
  IconButton,
  inputBaseClasses,
  inputLabelClasses,
  listItemClasses,
  Stack,
  styled,
  useMediaQuery,
} from '@mui/material';
import { CalendarIcon, DateRangePicker, multiInputDateRangeFieldClasses, pickersLayoutClasses } from '@mui/x-date-pickers-pro';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { Button } from '@/components/button/button';
import type { DateRangeTuple } from '@/components/table/common/date_filter';
import { muiTheme } from '@/theme';
import { localeDateFormat } from '@/utils/localeDateFormat';

export type { DateRangeTuple };

// Field order/separators follow the browser locale (DD/MM vs MM/DD vs ISO);
// calendar text follows the UI language via the LocalizationProvider's adapterLocale.
const dateFieldFormat = localeDateFormat();

const SM_UP = muiTheme.breakpoints.up('sm');

const StyledDateRangePicker = styled(DateRangePicker)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  [`&.${multiInputDateRangeFieldClasses.root}`]: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 460,
    },
  },
  [`&.${multiInputDateRangeFieldClasses.root}>:not(style)~:not(style)`]: {
    marginLeft: theme.spacing(1),
  },
  [`& .${inputBaseClasses.input}`]: {
    paddingTop: '6px',
    paddingBottom: '6px',
    [theme.breakpoints.up('sm')]: {
      paddingTop: '8px',
      paddingBottom: '8px',
    },
    height: 'auto',
    fontSize: '0.75rem',
    lineHeight: '133%',
  },
  [`.${inputLabelClasses.root}:not(.${inputLabelClasses.shrink})`]: {
    top: -2,
  },
}));

const StyledCalendarIcon = styled(CalendarIcon)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
  color: theme.vars.palette.action.active,
}));

const shortcutsSlotProps = {
  sx: {
    [`&.${pickersLayoutClasses.shortcuts}`]: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      borderTopLeftRadius: 8,
      borderBottomLeftRadius: 8,
      backgroundColor: 'background.paper-elevation-3',
    },
    [`& .${listItemClasses.root}`]: {
      p: 0,
      [` .${chipClasses.root}`]: {
        height: '36px',
        px: 2,
        py: '6px',
        borderRadius: 0,
        width: '100%',
        textAlign: 'left',
        justifyContent: 'flex-start',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'primary._states.hover',
        },
        [`& .${chipClasses.label}`]: {
          p: 0,
        },
      },
    },
  },
};

const popperSlotProps = { popperOptions: { modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] } };

const shortcutMessages = {
  today: msg({ message: 'Today', comment: 'Filter: Today' }),
  last7Days: msg({ message: 'Last 7 Days', comment: 'Filter: Last 7 Days' }),
  last30Days: msg({ message: 'Last 30 Days', comment: 'Filter: Last 30 Days' }),
  thisYear: msg({ message: 'This Year', comment: 'Filter: This Year' }),
  lastYear: msg({ message: 'Last Year', comment: 'Filter: Last Year' }),
  allTime: msg({ message: 'All Time', comment: 'Filter: All Time' }),
  reset: msg({ message: 'Reset', comment: 'Filter: Reset' }),
};

const shortcutRanges: Record<keyof typeof shortcutMessages, (now: Dayjs) => DateRangeTuple> = {
  today: (now) => [now.startOf('day'), now.endOf('day')],
  last7Days: (now) => [now.subtract(6, 'day'), now],
  last30Days: (now) => [now.subtract(29, 'day'), now],
  thisYear: (now) => [now.startOf('year'), now],
  lastYear: (now) => [now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')],
  allTime: () => [null, null],
  reset: () => [null, null],
};

export function DateRangeFilter({
  onChange,
  dateRange,
}: {
  readonly dateRange?: DateRangeTuple;
  readonly onChange?: (value: DateRangeTuple) => void;
}) {
  const { t } = useLingui();
  const shortcutsItems = useMemo(
    () =>
      (Object.keys(shortcutRanges) as (keyof typeof shortcutRanges)[]).map((key) => ({
        label: t(shortcutMessages[key]),
        getValue: (): DateRangeTuple => shortcutRanges[key](dayjs()),
      })),
    [t]
  );

  const isDesktop = useMediaQuery(SM_UP, { defaultMatches: true });

  // Emit only a full range or a full clear; a half-picked range stays pending.
  const handleChange = useCallback(
    (value: [unknown, unknown] | null) => {
      const [start, end] = (value ?? [null, null]) as DateRangeTuple;
      if (start && end) onChange?.([start, end]);
      else if (!start && !end) onChange?.([null, null]);
    },
    [onChange]
  );

  const handleReset = useCallback(() => onChange?.([null, null]), [onChange]);

  return (
    <Stack direction="row" spacing={1}>
      <StyledDateRangePicker
        disableFuture
        desktopModeMediaQuery={SM_UP}
        value={dateRange ?? [null, null]}
        localeText={{
          start: t({ message: 'Start date', comment: 'Filter: Start date' }),
          end: t({ message: 'End date', comment: 'Filter: End date' }),
        }}
        format={dateFieldFormat}
        formatDensity="dense"
        slotProps={{
          shortcuts: {
            items: isDesktop ? shortcutsItems : undefined,
            ...shortcutsSlotProps,
          },
          field: {
            dateSeparator: '—',
          },
          popper: popperSlotProps,
          textField: {
            size: 'small',
            label: '',
            InputProps: {
              endAdornment: <StyledCalendarIcon fontSize="small" />,
            },
          },
        }}
        onChange={handleChange}
      />
      {isDesktop ? (
        <Button variant="text" size="medium" sx={{ p: 0.5 }} onClick={handleReset}>
          {t(shortcutMessages.reset)}
        </Button>
      ) : (
        <IconButton size="small" sx={{ p: 0.5 }} onClick={handleReset}>
          <Cancel />
        </IconButton>
      )}
    </Stack>
  );
}
